import { useNavigate } from 'react-router';
import { BarChart3, Target, TrendingUp, User, CheckSquare, ArrowRight, PieChart, FileText } from 'lucide-react';
import { CurriculumHeader } from '../components/CurriculumHeader';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';

interface ReportTile {
  id: string;
  title: string;
  description: string;
  icon: React.ElementType;
  color: string;
  bgColor: string;
  borderColor: string;
}

export function ReportsPage() {
  const navigate = useNavigate();

  const activeReportTiles: ReportTile[] = [
    {
      id: 'annual-progress',
      title: 'Annual Progress',
      description: 'See how many topics are completed vs remaining across the curriculum',
      icon: PieChart,
      color: 'text-action-blue',
      bgColor: 'bg-action-blue/10',
      borderColor: 'border-action-blue/30'
    },
    {
      id: 'assessment-coverage',
      title: 'Assessment Coverage',
      description: 'Overview of completed assessments and pending evaluations',
      icon: CheckSquare,
      color: 'text-indigo-600',
      bgColor: 'bg-indigo-50',
      borderColor: 'border-indigo-200'
    },
    {
      id: 'learning-activity-audit',
      title: 'Learning Activity Audit',
      description: 'Detailed record of completed units, lessons, topics, objectives, and assessments for compliance reporting',
      icon: FileText,
      color: 'text-teal-600',
      bgColor: 'bg-teal-50',
      borderColor: 'border-teal-200'
    }
  ];

  const comingSoonReportTiles: ReportTile[] = [
    {
      id: 'gcse-coverage',
      title: 'GCSE Coverage',
      description: 'Track coverage of GCSE curriculum topics and requirements',
      icon: Target,
      color: 'text-confirm-action',
      bgColor: 'bg-confirm-action/10',
      borderColor: 'border-confirm-action/30'
    },
    {
      id: 'strengths-weaknesses',
      title: 'Strengths & Weaknesses',
      description: 'Analyze performance patterns across different subjects',
      icon: TrendingUp,
      color: 'text-purple-600',
      bgColor: 'bg-purple-50',
      borderColor: 'border-purple-200'
    },
    {
      id: 'profile-insights',
      title: 'Profile Insights',
      description: 'Personalized learning insights and recommendations',
      icon: User,
      color: 'text-orange-600',
      bgColor: 'bg-orange-50',
      borderColor: 'border-orange-200'
    }
  ];

  const handleReportClick = (reportId: string) => {
    navigate(`/app/reports/${reportId}`);
  };

  return (
    <div className="p-4 sm:p-6">
        <CurriculumHeader 
          showTabs={false}
          title="Reports"
          icon={BarChart3}
        />

        <p className="text-gray-600 mb-6">
          Access detailed reports and analytics about your learning progress
        </p>

        {/* Reports Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {activeReportTiles.map((report) => {
            const Icon = report.icon;
            return (
              <Card 
                key={report.id} 
                className={`hover:shadow-lg transition-all duration-200 cursor-pointer border-2 ${report.borderColor} hover:scale-105`}
                onClick={() => handleReportClick(report.id)}
              >
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div className={`p-3 rounded-lg ${report.bgColor}`}>
                      <Icon className={`${report.color}`} size={24} />
                    </div>
                    <ArrowRight className="text-gray-400" size={20} />
                  </div>
                  <CardTitle className="text-xl font-semibold text-gray-900 mt-4">
                    {report.title}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    {report.description}
                  </p>
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <span className="text-xs text-gray-500 uppercase tracking-wider font-medium">
                      Click to view report
                    </span>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* More Reports Coming Soon */}
        <div className="mt-12">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">More reports coming soon</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {comingSoonReportTiles.map((report) => {
              const Icon = report.icon;
              return (
                <Card 
                  key={report.id} 
                  className="opacity-50 cursor-not-allowed border-2 border-gray-200"
                >
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div className="p-3 rounded-lg bg-gray-100">
                        <Icon className="text-gray-400" size={24} />
                      </div>
                      <ArrowRight className="text-gray-300" size={20} />
                    </div>
                    <CardTitle className="text-xl font-semibold text-gray-400 mt-4">
                      {report.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-400 text-sm leading-relaxed">
                      {report.description}
                    </p>
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <span className="text-xs text-gray-400 uppercase tracking-wider font-medium">
                        Coming soon
                      </span>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </div>
  );
}